import { type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
  InfiniteData,
  keepPreviousData,
  Query,
  type QueryClient,
  type QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { max } from 'date-fns';

import { Endpoints, PriceAggregateWithConversions } from '@/api';
import { useFetch } from '@/utils/fetch';

import {
  getEstimatedDueAtDate as getArticleEstimatedDueAtDate,
  getNumberOfDefects as getArticleNumberOfDefects,
} from './article';

export const getEstimatedDueAtDate = (
  request: RequestWithRelations | RequestWithRelationsFromRequestsList
) => {
  const articleEstimatedDueAtDates = request.articles
    .filter((article) => !!getArticleEstimatedDueAtDate(article))
    .map((article) => getArticleEstimatedDueAtDate(article)!);

  if (articleEstimatedDueAtDates.length) {
    return max(articleEstimatedDueAtDates);
  }

  return null;
};

export const getArchivedAtDate = (
  request: RequestWithRelations | RequestWithRelationsFromRequestsList
) => {
  const articlesArchiveDates = request.allArticles
    .filter((article) => !!article.archivedAt)
    .map((article) => new Date(article.archivedAt!));

  // Find the article with the latest archivedAtDate
  return articlesArchiveDates.length === 0
    ? null
    : articlesArchiveDates.sort((a, b) => b.getTime() - a.getTime())[0];
};

export const isInDraftStep = (
  request: RequestWithRelations | RequestWithRelationsFromRequestsList
) =>
  hasActiveArticles(request) &&
  request.articles.every((article) => article.step?.step === 'creation');

export const isInFirstServiceChoiceStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some(
    (article) =>
      article.step?.step === 'service-choice' &&
      (article.steps ?? []).filter((step) => step.step === 'service-choice').length === 1
  );

export const isInLaterServiceChoiceStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some(
    (article) =>
      article.step?.step === 'service-choice' &&
      (article.steps ?? []).filter((step) => step.step === 'service-choice').length > 1
  );

export const isInValidationStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'validation' && !article.step.sync);

export const isInExchangeStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'exchange' && !article.step.sync);

export const isInQualityControlStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some(
    (article) => article.step?.step === 'quality-control' && !article.step.sync
  );

export const isInTransitStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'transit');

export const isInInitialTransitStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some(
    (article) =>
      article.step?.step === 'transit' &&
      (article.step.config.originType === 'client' || article.step.config.originType === 'store') &&
      article.step.config.destinationType === 'workshop'
  );

export const isInAnalysisStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'analysis');

export const isOnlyArticleInPaymentStep = (request: RequestWithRelations, articleId: string) => {
  const articlesInPaymentStep = request.articles.filter(
    (article) => article.step?.step === 'payment'
  );

  return (
    hasActiveArticles(request) &&
    articlesInPaymentStep.length === 1 &&
    articlesInPaymentStep[0].id === articleId
  );
};

export const isOnlyArticleInRefundtStep = (request: RequestWithRelations, articleId: string) => {
  const articlesInRefundStep = request.articles.filter(
    (article) => article.step?.step === 'refund'
  );

  return (
    hasActiveArticles(request) &&
    articlesInRefundStep.length === 1 &&
    articlesInRefundStep[0].id === articleId
  );
};

export const isInPaymentStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'payment');

export const isInRefundStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) && request.articles.some((article) => article.step?.step === 'refund');

export const isInLegacyPaymentStepWithValidation = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some(
    (article) =>
      article.step?.step === 'payment' &&
      article.step?.config.method !== 'external' &&
      'legacyPaymentStepWithValidation' in article.step.config &&
      !!article.step?.config.legacyPaymentStepWithValidation
  );

export const isInRepairStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) && request.articles.some((article) => article.step?.step === 'repair');

export const isInProcessingStep = (request: RequestWithRelations) =>
  hasActiveArticles(request) &&
  request.articles.some((article) => article.step?.step === 'processing');

export const isInCompletedStep = (request: RequestWithRelations) =>
  request.allArticles.every((article) =>
    article.step
      ? article.step.step === 'archival'
      : !!article.archivedAt && article.archivalDetail?.type === 'completed'
  );

export const isArchived = (request: RequestWithRelations | RequestWithRelationsFromRequestsList) =>
  request.allArticles.every((article) => !!article.archivedAt);

export const isManuallyArchived = (request: RequestWithRelations) =>
  request.allArticles.every((article) => article.archivalDetail?.type === 'manual');

export const isAutomaticallyArchived = (request: RequestWithRelations) =>
  request.allArticles.every((article) => article.archivalDetail?.type === 'automatic');

export const isArchivedAndExportedToZendesk = (request: RequestWithRelations) =>
  (isManuallyArchived(request) || isAutomaticallyArchived(request)) &&
  request.allArticles.every(
    (article) =>
      article.archivalDetail?.exportedTo === 'zendesk' ||
      article.archivalDetail?.reason === 'export-to-zendesk'
  );

export const isCancelled = (request: RequestWithRelations) =>
  request.allArticles.every((article) => !!article.cancelledAt);

const hasActiveArticles = (request: RequestWithRelations | RequestWithRelationsFromRequestsList) =>
  request.articles.length > 0;

export const getNumberOfDefects = (request: RequestWithRelations) =>
  request.articles.reduce(
    (requestQuantity, article) => requestQuantity + getArticleNumberOfDefects(article),
    0
  );

export const REQUESTOR_TYPES = [
  {
    id: 'client',
    label: msg({ id: 'request.requestor.type.client', message: 'Online client' }),
  },
  {
    id: 'client-via-store',
    label: msg({ id: 'request.requestor.type.client-via-store', message: 'In-store client' }),
  },
  {
    id: 'store',
    label: msg({ id: 'request.requestor.type.store', message: 'Store' }),
  },
] as const;

export type RequestorType = (typeof REQUESTOR_TYPES)[number]['id'];

export const getRequestorType = (request: {
  client: null | { id: string };
  storeId: string | null;
}) =>
  request.client && request.storeId ? 'client-via-store' : request.client ? 'client' : 'store';

export const getRequestorTypeLabel = (
  requestRequestorType: 'client' | 'store' | 'client-via-store'
): MessageDescriptor => {
  return REQUESTOR_TYPES.find((requestType) => requestType.id === requestRequestorType)!.label;
};

// Note: We are waiting for more info form the product team regarding which options to add here
export const REQUEST_WAIVE_HANDLING_FEE_REASONS = [
  {
    id: 'loyalty-commercial-gesture',
    label: msg({
      id: 'request.waive-handling-fee-reason.loyalty-commercial-gesture',
      message: 'Loyalty commercial gesture',
    }),
  },
  {
    id: 'in-store-damage',
    label: msg({
      id: 'request.waive-handling-fee-reason.in-store-damage',
      message: 'In-store damage',
    }),
  },
  {
    id: 'purchase-history-error',
    label: msg({
      id: 'request.waive-handling-fee-reason.purchase-history-error',
      message: 'Purchase history error',
    }),
  },
  {
    id: 'item-received-as-a-gift',
    label: msg({
      id: 'request.waive-handling-fee-reason.item-received-as-a-gift',
      message: 'Item received as a gift',
    }),
  },
  {
    id: 'other',
    label: msg({
      id: 'request.waive-handling-fee-reason.other',
      message: 'Other',
    }),
  },
] as const;

export type FeedbackRating = 1 | 2 | 3 | 4 | 5;
export type Feedback = {
  global?: FeedbackRating;
  articlesFeedback?: { articleId: string; quality: FeedbackRating }[];
  quality?: FeedbackRating;
  speed?: FeedbackRating;
  communication?: FeedbackRating;
  comment?: string;
  acceptContact?: boolean;
};

const formatActionWithRelations = (
  action: Endpoints['GET /requests/:id']['response']['articles'][number]['snapshot']['articleActions'][number] & {
    workshopPrice: PriceAggregateWithConversions | undefined;
    organizationPrice: PriceAggregateWithConversions | undefined;
    media: Endpoints['GET /requests/:id']['response']['articles'][number]['media'];
  }
) => {
  const sparePartsWorkshopPrice = action.workshopPrice?.components.find(
    (component) => component.subType === 'spare-parts'
  );

  const sparePartsOrganizationPrice = action.organizationPrice?.components.find(
    (component) => component.subType === 'spare-parts'
  );

  return {
    ...action,
    spareParts: action.spareParts.map((sparePart) => ({
      ...sparePart,
      workshopPrice: getPriceComponent(sparePartsWorkshopPrice, sparePart.id),
      organizationPrice: getPriceComponent(sparePartsOrganizationPrice, sparePart.id),
    })),
  };
};

const formatArticleSnapshotWithRelations = (
  snapshot: Endpoints['GET /requests/:id']['response']['articles'][number]['snapshot'] & {
    workshopPrice: PriceAggregateWithConversions | undefined;
    organizationPrice: PriceAggregateWithConversions | undefined;
  },
  articleMedia: Endpoints['GET /requests/:id']['response']['articles'][number]['media']
) => ({
  ...snapshot,
  articleDefects: snapshot.articleDefects.map((defect) => ({
    ...defect,
    media: articleMedia.filter((medium) =>
      medium.articleDefects?.find(({ id }) => id === defect.id)
    ),
  })),
  articleActions: snapshot.articleActions.map((action) =>
    formatActionWithRelations({
      ...action,
      workshopPrice: getPriceComponent(snapshot.workshopPrice, action.id),
      organizationPrice: getPriceComponent(snapshot.organizationPrice, action.id),
      media: articleMedia.filter((medium) =>
        medium.articleActions?.find(({ id }) => id === action.id)
      ),
    })
  ),
  articleExchange: snapshot.articleExchange && {
    ...snapshot.articleExchange,
    workshopPrice: getPriceComponent(snapshot.workshopPrice, snapshot.articleExchange.id),
    organizationPrice: getPriceComponent(snapshot.organizationPrice, snapshot.articleExchange.id),
  },
  fees: snapshot.fees.map((fee) => ({
    ...fee,
    workshopPrice: getPriceComponent(snapshot.workshopPrice, fee.id),
    organizationPrice: getPriceComponent(snapshot.organizationPrice, fee.id),
  })),
});

const formatArticleWithRelations = (
  article: Endpoints['GET /requests/:id']['response']['articles'][number],
  request: Endpoints['GET /requests/:id']['response']
) => ({
  ...article,
  requestId: (article.requestId ?? article.archivedRequestId)!,
  snapshot: formatArticleSnapshotWithRelations(
    {
      ...article.snapshot,
      workshopPrice: getPriceComponent(request.workshopPrice, article.id),
      organizationPrice: getPriceComponent(request.organizationPrice, article.id),
    },
    article.media
  ),
  previousSnapshot: article.previousSnapshot
    ? formatArticleSnapshotWithRelations(
        {
          ...article.previousSnapshot,
          workshopPrice: getPriceComponent(request.previousWorkshopPrice, article.id),
          organizationPrice: getPriceComponent(request.previousOrganizationPrice, article.id),
        },
        article.media
      )
    : undefined,
});

const formatRequestFeeWithRelations = (
  fee: Endpoints['GET /requests/:id']['response']['fees'][number],
  request: Endpoints['GET /requests/:id']['response']
) => ({
  ...fee,
  workshopPrice: getPriceComponent(request.workshopPrice, fee.id),
  organizationPrice: getPriceComponent(request.organizationPrice, fee.id),
});

export const formatRequestWithRelations = (request: Endpoints['GET /requests/:id']['response']) => {
  const formattedRequest = {
    ...request,
    deposits: request.deposits ?? [],
    articles: request.articles.map((article) => formatArticleWithRelations(article, request)),
    archivedArticles: request.archivedArticles.map((article) =>
      formatArticleWithRelations(article, request)
    ),
    fees: request.fees.map((fee) => formatRequestFeeWithRelations(fee, request)),
  };

  return {
    ...formattedRequest,
    allArticles: [...formattedRequest.articles, ...formattedRequest.archivedArticles],
  };
};

const formatRequestWithRelationsFromRequestsList = (
  request: Endpoints['GET /requests']['response']['requests'][number]
) => {
  return {
    ...request,
    allArticles: [...request.articles, ...request.archivedArticles],
  };
};

const getPriceComponent = (price: PriceAggregateWithConversions | null | undefined, id: string) =>
  price?.components.find((component) => component.componentId === id);

export type RequestWithRelations = ReturnType<typeof formatRequestWithRelations>;
export type RequestWithRelationsFromRequestsList = ReturnType<
  typeof formatRequestWithRelationsFromRequestsList
>;
export type ArticleWithRelations = RequestWithRelations['articles'][number];
export type ArticleWithRelationsFromRequestsList =
  RequestWithRelationsFromRequestsList['articles'][number];

type ArticleSnapshotWithRelations = ArticleWithRelations['snapshot'];

export type ArticleDefectWithRelations = ArticleSnapshotWithRelations['articleDefects'][number];
export type ArticleActionWithRelations = ArticleSnapshotWithRelations['articleActions'][number];
export type ProductAttributeValueWithParent =
  ArticleWithRelations['productAttributeValues'][number];

export const useRequests = (
  params: Endpoints['GET /requests']['query'] = {},
  options: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /requests']>();

  return useQuery({
    queryKey: ['requests', params],
    // Pass the signal so that outdated requests (e.g. while typing a search) are cancelled
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetch('/requests', params, { method: 'GET', signal }).then(({ requests, meta }) => ({
        requests: requests.map(formatRequestWithRelationsFromRequestsList),
        meta,
      })),
    enabled: options.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useRequest = (
  id?: string,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<RequestWithRelations, Error, RequestWithRelations, (string | undefined)[]>
        ) => number | false | undefined);
  }
) => {
  const fetch = useFetch<Endpoints['GET /requests/:id']>();

  return useQuery({
    queryKey: ['requests', id],
    queryFn: () => fetch(`/requests/${id!}`).then(formatRequestWithRelations),
    enabled: !!id && options?.enabled !== false,
    refetchInterval: () => (document.visibilityState === 'hidden' ? 5 * 60 * 1000 : 30_000), // Refetch request every five minutes if the focus is outside, every 30 seconds otherwise
    refetchIntervalInBackground: true,
    ...options,
  });
};

export const useClientAmountDue = ({
  requestId,
  enabled = true,
}: {
  requestId: string;
  enabled?: boolean;
}) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/client-amount-due']>();

  return useQuery({
    queryKey: ['requests', requestId, 'client-amound-due'],
    queryFn: () => fetch(`/requests/${requestId}/client-amount-due`),
    enabled,
  });
};

export const useClientRequest = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /requests/:id']>();

  return useQuery({
    queryKey: ['requests', id],
    queryFn: () => fetch(`/requests/${id!}`).then(formatRequestWithRelations),
    enabled: !!id,
  });
};

export const useCreateDraftRequest = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/draft']>();

  return useMutation({
    mutationFn: () => fetch('/requests/draft', undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useUpdateDraftRequest = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/draft/:id']>();

  return useMutation({
    mutationFn: (data: Endpoints['PATCH /requests/draft/:id']['body']) =>
      fetch(`/requests/draft/${id}`, undefined, {
        method: 'PATCH',
        body: data,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useUpdateRequestType = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/type']>();

  return useMutation({
    mutationFn: (data: Endpoints['PATCH /requests/:id/type']['body']) =>
      fetch(`/requests/${id}/type`, undefined, {
        method: 'PATCH',
        body: data,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useUpdateRequestExternalPaymentReference = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/external-payment-reference']>();

  return useMutation({
    mutationFn: (data: Endpoints['PATCH /requests/:id/external-payment-reference']['body']) =>
      fetch(`/requests/${requestId}/external-payment-reference`, undefined, {
        method: 'PATCH',
        body: data,
      }),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['requests'] });

      queryClient
        .getQueriesData<RequestWithRelations | { requests: RequestWithRelations[] }>({
          queryKey: ['requests'],
        })
        .forEach(([queryKey, queryData]) => {
          if (queryData && 'requests' in queryData) {
            // Optimistic update for useRequests query
            queryClient.setQueryData(queryKey, {
              ...queryData,
              requests: queryData.requests.map((request) =>
                request.id === requestId
                  ? Object.assign(request, {
                      externalPaymentReference: data.externalPaymentReference,
                    })
                  : request
              ),
            });
          } else if (queryData && queryKey[1] === requestId) {
            // Optimistic update for useRequest query
            queryClient.setQueryData(
              queryKey,
              Object.assign(queryData, { externalPaymentReference: data.externalPaymentReference })
            );
          }
        });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['client-payment-transactions'] });
    },
  });
};

export const useUpdateRequestSupervisor = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/supervisor']>();

  return useMutation({
    mutationFn: ({ supervisor }: { supervisor: { id: string } | null }) =>
      fetch(`/requests/${requestId}/supervisor`, undefined, {
        method: 'PATCH',
        body: { supervisorId: supervisor?.id || null },
      }),
    onMutate: async ({ supervisor }) => {
      await queryClient.cancelQueries({ queryKey: ['requests'] });

      queryClient
        .getQueriesData<RequestWithRelations | { requests: RequestWithRelations[] }>({
          queryKey: ['requests'],
        })
        .forEach(([queryKey, queryData]) => {
          if (queryData && 'requests' in queryData) {
            // Optimistic update for useRequests query
            queryClient.setQueryData(queryKey, {
              ...queryData,
              requests: queryData.requests.map((request) =>
                request.id === requestId ? Object.assign(request, { supervisor }) : request
              ),
            });
          } else if (queryData && queryKey[1] === requestId) {
            // Optimistic update for useRequest query
            queryClient.setQueryData(queryKey, Object.assign(queryData, { supervisor }));
          }
        });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

// Creating and deleting the articles of a draft request share a scope so they run serially.
// Otherwise, a creation sent while a deletion is still pending (the article being already removed
// from the cache optimistically) can be rejected by the max articles per request rule.
const getDraftRequestArticleMutationScope = (requestId: string) => ({
  id: `draft-request-article-${requestId}`,
});

export const useCreateDraftRequestArticle = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/draft/:id/articles']>();

  return useMutation({
    scope: getDraftRequestArticleMutationScope(id),
    mutationFn: (body?: Endpoints['POST /requests/draft/:id/articles']['body']) =>
      fetch(`/requests/draft/${id}/articles`, undefined, { method: 'POST', body }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useDeleteDraftRequestArticle = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch();

  return useMutation({
    scope: getDraftRequestArticleMutationScope(id),
    mutationFn: (articleId: string) =>
      fetch(`/requests/draft/${id}/articles/${articleId}`, undefined, { method: 'DELETE' }),
    onMutate: (articleId) => {
      queryClient
        .getQueriesData<RequestWithRelations | { requests: RequestWithRelations[] }>({
          queryKey: ['requests'],
        })
        .forEach(([queryKey, queryData]) => {
          if (queryData && 'requests' in queryData) {
            // Optimistic update for useRequests query
            queryClient.setQueryData(queryKey, {
              ...queryData,
              requests: queryData.requests.map((request) =>
                request.id === id
                  ? { ...request, articles: request.articles.filter((a) => a.id !== articleId) }
                  : request
              ),
            });
          } else if (queryData && queryKey[1] === id && queryKey.length === 2) {
            // Optimistic update for useRequest query
            queryClient.setQueryData(queryKey, {
              ...queryData,
              articles: queryData.articles.filter((a) => a.id !== articleId),
            });
          }
        });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useSendDraftRequest = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/draft/:id/send']>();

  return useMutation({
    mutationFn: (data: Endpoints['POST /requests/draft/:id/send']['body']) =>
      fetch(`/requests/draft/${id}/send`, undefined, { method: 'POST', body: data }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useCreateDraftGuestRequest = () => {
  const fetch = useFetch<Endpoints['POST /requests/draft-guest']>();

  return useMutation({
    mutationFn: (data: Endpoints['POST /requests/draft-guest']['body']) =>
      fetch('/requests/draft-guest', undefined, {
        method: 'POST',
        body: data,
      }),
  });
};

export const useSendClientRequest = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/send']>();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      fetch(`/requests/${id}/send`, undefined, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useUpdateRequestClient = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/client']>();

  return useMutation({
    mutationKey: ['requests', requestId, 'client'],
    mutationFn: (body: Endpoints['PATCH /requests/:id/client']['body']) => {
      const requestQueryKey = ['requests', requestId];
      const currentRequest = queryClient.getQueryData<RequestWithRelations>(requestQueryKey);

      if (currentRequest) {
        const updatedRequest = formatRequestWithRelations({
          ...currentRequest,
          client: currentRequest.client
            ? {
                ...currentRequest.client,
                ...(body.client
                  ? {
                      ...body.client,
                      address:
                        currentRequest.client.address && body.client.address
                          ? {
                              ...currentRequest.client.address,
                              ...body.client.address,
                            }
                          : null,
                      billingAddress:
                        currentRequest.client.billingAddress && body.client.billingAddress
                          ? {
                              ...currentRequest.client.billingAddress,
                              ...body.client.billingAddress,
                            }
                          : null,
                    }
                  : body.client === null
                    ? {
                        externalId: null,
                        name: null,
                        title: null,
                        firstName: null,
                        lastName: null,
                        email: null,
                        phone: null,
                        deliveryOption: null,
                        shippingChoice: null,
                        sendcloudServicePointId: null,
                        locale: null,
                        dataFromCrm: null,
                      }
                    : {}),
              }
            : null,
          vip: body.vip ?? currentRequest.vip,
        });

        queryClient.setQueryData(requestQueryKey, updatedRequest);
      }

      return fetch(`/requests/${requestId}/client`, undefined, { method: 'PATCH', body });
    },
    onSettled: (_data, _error, body) => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['workshops'] });

      if (body.client?.externalId) {
        queryClient.invalidateQueries({ queryKey: ['clients', 'purchase-history'] });
      }
    },
  });
};

export const useIsUpdatingClientExternalId = (requestId: string) => {
  const pendingClientUpdateMutations = useMutationState({
    filters: {
      mutationKey: ['requests', requestId, 'client'],
      status: 'pending',
    },
  });

  const isUpdatingExternalId = !!pendingClientUpdateMutations.some(
    (mutation) =>
      (mutation.variables as Endpoints['PATCH /requests/:id/client']['body'])?.client?.externalId
  );

  return isUpdatingExternalId;
};

export const useArchiveRequest = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /requests/:id']>();

  return useMutation({
    mutationFn: ({
      id,
      query,
    }: {
      id: string;
      query?: Endpoints['DELETE /requests/:id']['query'];
    }) => fetch(`/requests/${id}`, query, { method: 'DELETE' }),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['requests'] });

      queryClient
        .getQueriesData<{ requests: RequestWithRelations[] }>({ queryKey: ['requests'] })
        .forEach(([queryKey, queryData]) => {
          if (queryData && 'requests' in queryData) {
            // Optimistic update for useRequests query
            queryClient.setQueryData(queryKey, {
              ...queryData,
              requests: queryData.requests.filter((request) => request.id !== id),
            });
          }
        });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

type RequestActivity = Endpoints['GET /requests/:id/activities']['response']['activities'][number];

type RequestActivityType = RequestActivity['type'];
type RequestActivityOfType<T extends RequestActivityType> = Extract<RequestActivity, { type: T }>;

type UseActivitiesParams<T extends RequestActivityType> = {
  limit?: number;
  types?: T[];
};

type UseActivitiesData<T extends RequestActivityType> = {
  activities: RequestActivityOfType<T>[];
  meta: Endpoints['GET /requests/:id/activities']['response']['meta'];
};

export const useActivities = <T extends RequestActivityType>(
  { requestId, ...params }: { requestId: string } & UseActivitiesParams<T>,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<
            UseActivitiesData<T>,
            Error,
            UseActivitiesData<T>,
            (string | UseActivitiesParams<T>)[]
          >
        ) => number | false | undefined);
  }
) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/activities']>();

  return useQuery({
    queryKey: ['activities', requestId, params],
    queryFn: () => fetch<UseActivitiesData<T>>(`/requests/${requestId}/activities`, params),
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
};

export const useInfiniteActivities = ({ requestId }: { requestId: string }) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/activities']>();

  return useInfiniteQuery<
    Endpoints['GET /requests/:id/activities']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /requests/:id/activities']['response']>,
    QueryKey,
    string | null
  >({
    queryFn: ({ pageParam }) => {
      const requestParams = pageParam ? { before: pageParam } : undefined;

      return fetch(`/requests/${requestId}/activities`, requestParams);
    },
    queryKey: ['activities', requestId],
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.meta.next,
  });
};

export const useComments = ({
  requestId,
  articleId,
  enabled,
  ...params
}: {
  requestId: string;
  articleId?: string;
  enabled?: boolean;
} & (
  | Endpoints['GET /requests/:id/comments']
  | Endpoints['GET /requests/:id/articles/:articleId/comments']
)['query']) => {
  const fetch = useFetch<
    | Endpoints['GET /requests/:id/comments']
    | Endpoints['GET /requests/:id/articles/:articleId/comments']
  >();

  return useQuery({
    queryKey: ['comments', requestId, articleId, params],
    queryFn: async () => {
      // FIXME: Is this limit ok? Should we handle pagination?
      const defaultParams = { limit: 250, offset: 0 };

      return fetch(
        articleId
          ? `/requests/${requestId}/articles/${articleId}/comments`
          : `/requests/${requestId}/comments`,
        { ...defaultParams, ...params }
      );
    },
    refetchInterval: () =>
      document.visibilityState === 'hidden' ? 10 * 60 * 1_000 : 50 * 60 * 1_000, // Refetch every ten minutes if the focus is outside, every 5 minutes otherwise
    refetchIntervalInBackground: true,
    enabled,
  });
};

export const useUpdateComment = ({
  requestId,
  articleId,
}: {
  requestId: string;
  articleId?: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/comments/:commentId']>();

  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) => {
      return fetch(
        articleId
          ? `/requests/${requestId}/articles/${articleId}/comments/${commentId}`
          : `/requests/${requestId}/comments/${commentId}`,
        undefined,
        {
          method: 'PATCH',
          body: { content },
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', requestId] });
    },
  });
};

export const useDeleteComment = ({
  requestId,
  articleId,
}: {
  requestId: string;
  articleId?: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /requests/:id/comments/:commentId']>();

  type CommentsQueryData =
    | Endpoints['GET /requests/:id/articles/:articleId/comments']['response']
    | Endpoints['GET /requests/:id/comments']['response'];

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string }) => {
      const commentsQueries = queryClient.getQueriesData<CommentsQueryData>({
        queryKey: articleId ? ['comments', requestId, articleId] : ['comments', requestId],
      });

      commentsQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) {
          return;
        }

        const comments = queryData.comments.filter((comment) => comment.id !== commentId);

        queryClient.setQueryData(queryKey, {
          comments,
          meta: {
            ...queryData.meta,
            count: Math.max(0, queryData.meta.count - 1),
          },
        });
      });

      return fetch(
        articleId
          ? `/requests/${requestId}/articles/${articleId}/comments/${commentId}`
          : `/requests/${requestId}/comments/${commentId}`,
        undefined,
        {
          method: 'DELETE',
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', requestId] });
    },
  });
};

export const useCreateComment = ({
  requestId,
  articleId,
}: {
  requestId: string;
  articleId?: string;
}) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/comments']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/:id/comments']['body']) => {
      return fetch(
        articleId
          ? `/requests/${requestId}/articles/${articleId}/comments`
          : `/requests/${requestId}/comments`,
        undefined,
        {
          method: 'POST',
          body,
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', requestId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useValidateExternalPayment = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/validate-external-payment']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/requests/${id}/validate-external-payment`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
      await queryClient.invalidateQueries({ queryKey: ['client-payment-transactions'] });
    },
  });
};

export const useValidateExternalRefund = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/validate-external-refund']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`/requests/${id}/validate-external-refund`, undefined, { method: 'POST' }),
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
      await queryClient.invalidateQueries({ queryKey: ['client-payment-transactions'] });
    },
  });
};

export const useSendClientReminder = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/client-reminder']>();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Endpoints['POST /requests/:id/client-reminder']['body'];
    }) => fetch(`/requests/${id}/client-reminder`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

export const useIsCompletingValidation = (requestId: string) => {
  const pendingCompleteValidationMutations = useMutationState({
    filters: {
      mutationKey: ['requests', requestId, 'complete-validation'],
      exact: true,
      status: 'pending',
    },
  });

  const isCompletingValidation = !!pendingCompleteValidationMutations.length;

  return isCompletingValidation;
};

export const useCompleteValidation = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/complete-validation']>();

  return useMutation({
    mutationKey: ['requests', requestId, 'complete-validation'],
    mutationFn: () =>
      fetch(`/requests/${requestId}/complete-validation`, undefined, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const usePaymentChoice = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/payment-choice']>();

  return useMutation({
    mutationFn: ({
      id,
      choices,
    }: {
      id: string;
      choices: Endpoints['POST /requests/:id/payment-choice']['body']['choices'];
    }) =>
      fetch(`/requests/${id}/payment-choice`, undefined, {
        method: 'POST',
        body: { choices },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const usePayment = ({ requestId }: { requestId: string }) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/payment']>();

  return useQuery({
    queryKey: ['requests', requestId, 'payment'],
    queryFn: () => fetch(`/requests/${requestId}/payment`, undefined),
  });
};

export const useStartPayment = () => {
  const fetch = useFetch<Endpoints['POST /requests/:id/payment']>();

  return useMutation({
    mutationFn: ({ requestId }: { requestId: string }) =>
      fetch(`/requests/${requestId}/payment`, undefined, {
        method: 'POST',
      }),
  });
};

export type CheckoutData = Endpoints['POST /requests/:id/payment']['response'];

export const useCompletePendingClientPickup = ({ requestId }: { requestId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/complete-pending-client-pickup']>();

  return useMutation({
    mutationFn: () =>
      fetch(`/requests/${requestId}/complete-pending-client-pickup`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useRequestInvoice = (requestId: string, { enabled }: { enabled?: boolean } = {}) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/invoice']>();

  return useQuery({
    queryKey: ['requests', requestId, 'invoice'],
    queryFn: () => fetch(`/requests/${requestId}/invoice`),
    enabled,
  });
};

export const useSubmitFeedback = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/feedback']>();

  return useMutation({
    mutationFn: (feedback: Feedback) =>
      fetch(`/requests/${requestId}/feedback`, undefined, {
        method: 'POST',
        body: feedback,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

const setRequestHasSubscribedInCache = ({
  queryClient,
  requestId,
  hasSubscribed,
}: {
  queryClient: QueryClient;
  requestId: string;
  hasSubscribed: boolean;
}): void => {
  queryClient
    .getQueriesData<RequestWithRelations | { requests: RequestWithRelations[] }>({
      queryKey: ['requests'],
    })
    .forEach(([queryKey, queryData]) => {
      if (queryData && 'requests' in queryData) {
        queryClient.setQueryData(queryKey, {
          ...queryData,
          requests: queryData.requests.map((request) =>
            request.id === requestId ? { ...request, hasSubscribed } : request
          ),
        });
      } else if (queryData && queryKey.length === 2 && queryKey[1] === requestId) {
        queryClient.setQueryData(queryKey, { ...queryData, hasSubscribed });
      }
    });
};

export const useSubscribeToRequest = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch();

  return useMutation({
    mutationFn: () => fetch(`/requests/${requestId}/subscribe`, undefined, { method: 'POST' }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['requests'] });

      setRequestHasSubscribedInCache({ queryClient, requestId, hasSubscribed: true });
    },
    onError: () => {
      // Only revert this request: restoring a whole snapshot would drop concurrent updates
      setRequestHasSubscribedInCache({ queryClient, requestId, hasSubscribed: false });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useUnsubscribeFromRequest = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch();

  return useMutation({
    mutationFn: () => fetch(`/requests/${requestId}/unsubscribe`, undefined, { method: 'POST' }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['requests'] });

      setRequestHasSubscribedInCache({ queryClient, requestId, hasSubscribed: false });
    },
    onError: () => {
      // Only revert this request: restoring a whole snapshot would drop concurrent updates
      setRequestHasSubscribedInCache({ queryClient, requestId, hasSubscribed: true });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export type BatchResultResponse = Endpoints['POST /requests/bulk-supervisor']['response'];

const invalidateBulkRequestQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['requests'] });
  queryClient.invalidateQueries({ queryKey: ['activities'] });
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
};

export const useBulkUpdateSupervisor = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/bulk-supervisor']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/bulk-supervisor']['body']) =>
      fetch('/requests/bulk-supervisor', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkRequestQueries(queryClient);
    },
  });
};

export const useBulkArchiveRequests = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/bulk-archive']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/bulk-archive']['body']) =>
      fetch('/requests/bulk-archive', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkRequestQueries(queryClient);
    },
  });
};

export const useBulkSubscribeRequests = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/bulk-subscribe']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/bulk-subscribe']['body']) =>
      fetch('/requests/bulk-subscribe', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkRequestQueries(queryClient);
    },
  });
};

export const useBulkUnsubscribeRequests = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/bulk-unsubscribe']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/bulk-unsubscribe']['body']) =>
      fetch('/requests/bulk-unsubscribe', undefined, { method: 'POST', body }),
    onSettled: () => {
      invalidateBulkRequestQueries(queryClient);
    },
  });
};

export const useCanClaimRequest = ({
  requestId,
  clientToken,
}: {
  requestId?: string | null;
  clientToken?: string | null;
}) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/claim']>();

  return useQuery({
    queryKey: ['requests', requestId, 'claim', clientToken],
    queryFn: () =>
      fetch(`/requests/${requestId}/claim`, { clientToken: clientToken! }, { method: 'GET' }),
    enabled: !!requestId && !!clientToken,
    retry: false,
  });
};

export const useClaimRequest = ({ requestId }: { requestId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/claim']>();

  return useMutation({
    mutationFn: ({ clientToken }: { clientToken: string }) =>
      fetch(`/requests/${requestId}/claim`, undefined, {
        method: 'POST',
        body: { clientToken },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });
};

export const useSignRequestPickUpReceipt = ({ requestId }: { requestId: string | undefined }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /requests/:id/sign-pick-up-receipt']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /requests/:id/sign-pick-up-receipt']['body']) =>
      fetch(`/requests/${requestId}/sign-pick-up-receipt`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId, 'documents'] });
    },
  });
};

export const useRequestDocuments = ({ requestId }: { requestId: string }) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/documents']>();

  return useQuery({
    queryKey: ['requests', requestId, 'documents'],
    queryFn: () => fetch(`/requests/${requestId}/documents`, undefined, { method: 'GET' }),
    enabled: !!requestId,
  });
};

export const useAfterSalesManager = (
  { requestId }: { requestId: string },
  { enabled }: { enabled?: boolean } = {}
) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/after-sales-manager']>();

  return useQuery({
    queryKey: ['requests', requestId, 'after-sales-manager'],
    queryFn: () => fetch(`/requests/${requestId}/after-sales-manager`, undefined),
    enabled,
  });
};

export type DepositOptions = Endpoints['GET /requests/:id/deposit-options']['response'];

export const isRequiredPaidEstimateAdvanceDeposit = (depositOptions?: DepositOptions) =>
  !!depositOptions && depositOptions.type === 'paid-estimate-advance' && depositOptions.required;

export const useDepositOptions = ({
  requestId,
  enabled,
}: {
  requestId: string;
  enabled?: boolean;
}) => {
  const fetch = useFetch<Endpoints['GET /requests/:id/deposit-options']>();

  return useQuery({
    queryKey: ['requests', requestId, 'deposit-options'],
    queryFn: () => fetch(`/requests/${requestId}/deposit-options`, undefined, { method: 'GET' }),
    enabled: !!requestId && enabled,
  });
};

export const useUpdateDepositChoice = ({ requestId }: { requestId: string }) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /requests/:id/deposit']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /requests/:id/deposit']['body']) =>
      fetch(`/requests/${requestId}/deposit`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId, 'deposit-options'] });
    },
  });
};
